/**
 * Buyer-chat turn helpers: spoken filler, session goodbye, chat-card badge.
 * CommonJS so backend/node tests can require this file.
 */

const SHORT_THANKS_RE = /^(?:no thanks|thanks|thank you|thx|ty|cheers|appreciate it)([!. ]*)?$/i;
const SHORT_YES_NO_RE = /^(yes|yeah|yep|yup|no|nope|nah|ok|okay|sure|correct|right|that'?s right|that is right|sounds good|got it|perfect|please)([!. ]*)?$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SHORT_TIME_RE = /^(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)?|noon|midnight|morning|afternoon|evening|tonight|tomorrow|today|this (?:week|weekend)|monday|tuesday|wednesday|thursday|friday|saturday|sunday)(?:[, ].{0,24})?$/i;
const AREA_HINT_RE = /\b(st\.?\s*john|mount pearl|paradise|cbs|conception|torbay|portugal cove|avalon|gander|corner brook|labrador|newfoundland|downtown|georgestown|east end|west end|area|neighbourhood|neighborhood|community)\b/i;
const BIGGER_HINT_RE = /\b(show|see|find|search|listings?|homes?|houses?|propert\w*|tell me|what|where|how|why|when|budget|price|beds?|baths?|detail|more|for sale)\b/i;
const GOODBYE_RE = /\b(good\s*bye|goodbye|bye-bye|\bbye\b|that'?s all|that is all|i'?m done|i am done|we'?re done|i'?m all set|(i'?ll|i will|we'?ll|we will) (come|check) back|(i'?ll|i will|we'?ll) get back to you|talk (to you )?(later|soon)|another time|that'?s (it|enough) for (now|today))\b/i;
const GOODBYE_NOT_CLOSING_RE = /\b(show|see|more|another|other|next|tell me|book|schedule)\b/i;
const AVALON_BADGE_RE = /st\.?\s*john'?s and the avalon/i;

function normalizeTurnText(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^\w@.+':\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isSessionGoodbyePhrase(text) {
  const raw = String(text || '').trim();
  if (!raw || raw.length > 80) return false;
  if (raw.includes('?')) return false;
  if (GOODBYE_NOT_CLOSING_RE.test(raw)) return false;
  return GOODBYE_RE.test(raw);
}

/** Name / phone / email / time / yes-no / thanks — no spoken filler. */
function isShortIntakeTurn(text) {
  const raw = String(text || '').trim();
  if (!raw) return true;
  if (isSessionGoodbyePhrase(raw)) return true;

  const normalized = normalizeTurnText(raw);
  if (SHORT_THANKS_RE.test(normalized)) return true;
  if (SHORT_YES_NO_RE.test(normalized)) return true;
  if (EMAIL_RE.test(raw)) return true;

  const digits = raw.replace(/\D/g, '');
  if (
    digits.length >= 7
    && digits.length <= 11
    && raw.replace(/[\d\s().+-]/g, '').length <= 2
  ) {
    return true;
  }

  if (normalized.length <= 40 && SHORT_TIME_RE.test(normalized)) return true;

  // Area answers take a beat even when they are short place names.
  if (AREA_HINT_RE.test(normalized)) return false;

  if (
    !/[?]/.test(raw)
    && normalized.split(/\s+/).length <= 4
    && normalized.length <= 40
    && !BIGGER_HINT_RE.test(normalized)
    && !/\d/.test(raw)
    && /^(?:my name is |i'?m |it'?s )?[a-z][a-z'-]+(?: [a-z][a-z'-]+){0,3}$/i.test(normalized)
  ) {
    return true;
  }

  return false;
}

function wantsSpokenFiller(text) {
  return !isShortIntakeTurn(text);
}

function chatCardBadgeLabel(serviceAreaLabel) {
  const raw = String(serviceAreaLabel || '').trim();
  if (!raw) return 'Newfoundland and Labrador';
  // Pilot scope: St. John's-metro agents read as the local area, not the province.
  if (AVALON_BADGE_RE.test(raw) || /^st\.?\s*john'?s$/i.test(raw)) return "St. John's and surrounding areas";
  return raw;
}

module.exports = {
  isSessionGoodbyePhrase,
  isShortIntakeTurn,
  wantsSpokenFiller,
  chatCardBadgeLabel,
};
